/**
 * @fileoverview Navigation Components - Header bar and action buttons for ingestion flow.
 *
 * Components:
 * - BackButton: Navigate to previous step
 * - NextButton: Advance to next step (or "Create Matter" at step 0)
 * - FinishButton: Complete ingestion (visible on Review step when canFinish=true)
 * - MatterTitle: Breadcrumb header showing firm and matter name
 * - ContentNavigation: Combines Back/Next/Finish in a row
 */
import { ArrowLeft, ArrowRight, Check, ExternalLink, FileText, FileUp, Loader2 } from 'lucide-react';
import React, { useContext, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCompleteCaseIngestionMutation } from '@/services/api/caseIngestionService';
import { usePatchMatterMutation } from '@/services/api/mattersService';
import { getApiErrorMessage } from '@/utils/api-error';

import { ImportCmsDataDialog } from '../assist/ImportCmsDataDialog';
import { CREATION_STEP_COUNT, FIRST_INGESTION_STEP_INDEX, UNIFIED_STEPS } from '../constants';
import { CaseSetupMessagingContext } from '../context/CaseSetupMessagingContext';
import { useUnifiedFlow } from '../context/UnifiedFlowContext';

/**
 * Back button for header - navigates back, saving current form first if on audit step.
 * Uses requestNavigateToStep() to ensure consistent save-before-navigate behavior
 * across all navigation methods (Continue, Back, Sidebar).
 */
export const BackButton: React.FC = () => {
  const { state, requestNavigateToStep } = useUnifiedFlow();
  const { currentStepIndex, isCreatingMatter, isPatchingCaseInfo, isSavingStep } = state;

  const isFirstStep = currentStepIndex === 0;

  // Empty placeholder on first step to maintain layout
  if (isFirstStep) {
    return <div />;
  }

  const handleBack = () => {
    requestNavigateToStep(currentStepIndex - 1);
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={handleBack}
      disabled={isCreatingMatter || isPatchingCaseInfo || isSavingStep}
      className="h-7 text-xs text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white"
    >
      <ArrowLeft className="mr-1 h-3 w-3" />
      Back
    </Button>
  );
};

/**
 * Next/Continue button for header.
 * Uses outline variant for regular navigation, solid for "Create Matter" action.
 */
export const NextButton: React.FC = () => {
  const { state, goNext, requestNavigateToStep } = useUnifiedFlow();
  const { currentStepIndex, isCreatingMatter, isPatchingCaseInfo, isSavingStep, phase } = state;
  const isNavigatingRef = useRef(false);
  const [isNavigating, setIsNavigating] = useState(false);

  const isReviewStep = currentStepIndex === UNIFIED_STEPS.length - 1;
  const isCreationBoundary = currentStepIndex === CREATION_STEP_COUNT - 1;
  const isAuditTrackedStep = currentStepIndex >= FIRST_INGESTION_STEP_INDEX;

  // Hide Continue on Review step — only Finish is shown there
  if (isReviewStep && phase === 'ingestion') {
    return null;
  }

  const handleNext = async () => {
    if (isAuditTrackedStep) {
      requestNavigateToStep(currentStepIndex + 1);
      return;
    }
    if (isNavigatingRef.current) return;
    isNavigatingRef.current = true;
    setIsNavigating(true);
    try {
      await goNext();
    } finally {
      isNavigatingRef.current = false;
      setIsNavigating(false);
    }
  };

  const isLoading = isCreatingMatter || isPatchingCaseInfo || isSavingStep || isNavigating;

  const getNextLabel = () => {
    if (isSavingStep) return 'Saving';
    if (isPatchingCaseInfo) return 'Saving';
    if (!isCreationBoundary) return 'Continue';
    if (isCreatingMatter) return 'Creating';
    if (state.matterId) return 'Continue';
    return 'Create Matter';
  };

  return (
    <Button
      type="button"
      size="sm"
      onClick={handleNext}
      disabled={isLoading}
      className="h-7 text-xs min-w-[80px] bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100"
    >
      {isLoading ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <>
          {getNextLabel()}
          <ArrowRight className="ml-1 h-3 w-3" />
        </>
      )}
    </Button>
  );
};

/**
 * Full-width matter title bar above the layout.
 * Shows breadcrumb: "Firm › Matter" or "New Case Ingestion" before creation.
 * Uses Finch brand purple header styling.
 */
export const MatterTitle: React.FC = () => {
  const { state, selectedFirm } = useUnifiedFlow();

  const firmName = selectedFirm?.name;
  const caseName = state.matterName || 'New Case Ingestion';

  return (
    <div className="bg-header px-6 py-2">
      <div className="max-w-6xl mx-auto flex items-center gap-1.5">
        <h1 className="text-sm font-semibold text-white truncate">
          {firmName ? (
            <>
              <span className="text-white/70">{firmName}</span>
              <span className="text-white/40 mx-1.5">›</span>
              <span>{caseName}</span>
            </>
          ) : (
            caseName
          )}
        </h1>
        {state.matterId && (
          <Link
            to={`/matters/${state.matterId}`}
            className="inline-flex items-center flex-shrink-0 text-white/60 hover:text-white transition-colors"
            title="View Matter"
          >
            <ExternalLink className="h-3 w-3" />
          </Link>
        )}
      </div>
    </div>
  );
};


/**
 * Finish button - renders on Review step, disabled until all sections reviewed.
 * Sends any enabled intro messages (from CaseSetupMessagingContext) as part
 * of the completion flow, then navigates to the matter page.
 */
export const FinishButton: React.FC = () => {
  const { canFinish, state } = useUnifiedFlow();
  const navigate = useNavigate();
  const [isCompleting, setIsCompleting] = useState(false);
  const isCompletingRef = useRef(false);

  const [completeIngestion] = useCompleteCaseIngestionMutation();
  const [patchMatter] = usePatchMatterMutation();

  const isReviewStep = state.currentStepIndex === UNIFIED_STEPS.length - 1;

  // Messaging context is only available on the review step (when wrapped
  // by CaseSetupMessagingProvider). Returns null on other steps.
  const messaging = useContext(CaseSetupMessagingContext);

  // Only show on Review step
  if (!isReviewStep) {
    return null;
  }

  const handleFinish = async () => {
    const { matterId } = state;
    if (!matterId || !canFinish) return;
    if (isCompletingRef.current) return;
    isCompletingRef.current = true;
    setIsCompleting(true);
    try {
      // 1. Mark audit as completed
      await completeIngestion({ matterId }).unwrap();

      // 2. Update matter status to Initial Review
      await patchMatter({
        matter_id: matterId,
        patch: { status: 'Initial Review' },
      }).unwrap();

      // 3. Send intro messages fire-and-forget (after ingestion is committed)
      // Sent after completion so a retry won't duplicate messages.
      if (messaging?.willSendAnything) {
        messaging.sendMessages().then(({ successes, failures }) => {
          if (failures > 0) {
            // eslint-disable-next-line no-console
            console.error(`Failed to send ${failures} welcome message(s)`);
            toast.error('Failed to send a welcome message');
          }
          if (successes > 0 && failures === 0) {
            toast.success('Welcome message(s) sent');
          } else if (successes > 0) {
            toast.success('Some message(s) sent, but not all');
          }
        });
      }

      toast.success('Case ingestion completed');
      navigate(`/matters/${matterId}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to complete ingestion'));
    } finally {
      isCompletingRef.current = false;
      setIsCompleting(false);
    }
  };

  const isDisabled = !canFinish || isCompleting || (messaging?.draftsLoading ?? false);
  const buttonLabel = messaging?.willSendAnything ? 'Send & Complete' : 'Complete Ingestion';

  const button = (
    <Button
      type="button"
      size="sm"
      onClick={handleFinish}
      disabled={isDisabled}
      className="h-7 text-xs bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {isCompleting ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <>
          {buttonLabel}
          <Check className="ml-1 h-3 w-3" />
        </>
      )}
    </Button>
  );

  // Show tooltip when disabled due to incomplete sections
  if (!canFinish) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span role="button" tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p>Review all sections to finish</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return button;
};

/**
 * Content navigation - Back (left) + center actions + Continue / Finish (right).
 * Includes re-open sidebar button and import CTA when applicable.
 */
export const ContentNavigation: React.FC<{ showFinish?: boolean }> = ({ showFinish }) => {
  const { state, isAssistPanelOpen, setIsAssistPanelOpen, assistState, pendingImport, setPendingImport } = useUnifiedFlow();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const isFirstStep = state.currentStepIndex === 0;
  const { matterId } = state;

  // Show re-open button when import exists but panel is dismissed
  const showReopenButton = assistState.hasImport && !isAssistPanelOpen;
  // Show import CTA when no import exists (pre or post creation) and no pending import
  const showImportCta = isFirstStep && !assistState.hasImport && !assistState.isLoading && !pendingImport;

  return (
    <>
      <div className={`flex items-center ${isFirstStep ? 'justify-end' : 'justify-between'}`}>
        {!isFirstStep && <BackButton />}
        <div className="flex items-center gap-2">
          {/* Import CTA */}
          {showImportCta && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs gap-1.5"
              onClick={() => setShowImportDialog(true)}
            >
              <FileUp className="h-3 w-3" />
              Import CMS JSON
            </Button>
          )}
          {/* Re-open sidebar button */}
          {showReopenButton && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
                  onClick={() => setIsAssistPanelOpen(true)}
                >
                  <FileText className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Show CMS Assist</p>
              </TooltipContent>
            </Tooltip>
          )}
          <NextButton />
          {showFinish && <FinishButton />}
        </div>
      </div>

      {/* Import dialog */}
      <ImportCmsDataDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        matterId={matterId}
        onImportSuccess={() => setIsAssistPanelOpen(true)}
        onPendingImport={!matterId ? (data) => {
          setPendingImport(data);
          setIsAssistPanelOpen(true);
        } : undefined}
      />
    </>
  );
};
